import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

// Services
import { userService } from '../../services/users';
import { playlistService } from '../../services/playlists';
import { recordingsService, type TrackMatchSummary } from '../../services/recordings';
import {
  extractSpotifyTrackId as extractSpotifyTrackIdFromTrack,
  hydrateTracksWithMatches,
} from '../../utils/trackMatches';

// Interfaces
import type { RootState } from '../store';
import type { AppDispatch } from '../store';
import type { User } from '../../interfaces/user';
import type { Track } from '../../interfaces/track';
import type { Playlist, PlaylistItem, PlaylistItemWithSaved } from '../../interfaces/playlists';

const PLAYLIST_PAGE_SIZE = 100;
const SAVED_TRACKS_CHUNK_SIZE = 50;
const PLAYLIST_CACHE_PREFIX = 'cmf.playlist.';
const PLAYLIST_CACHE_VERSION = 1;
const PLAYLIST_CACHE_TTL_MS = 180 * 24 * 60 * 60 * 1000; // ~6 months
const PLAYLIST_CACHE_JITTER_MS = 24 * 60 * 60 * 1000;
const PLAYLIST_CACHE_MAX_ENTRIES = 30;
const canUseLocalStorage = typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const randomJitter = (): number => Math.floor(Math.random() * PLAYLIST_CACHE_JITTER_MS);

const makePlaylistCacheKey = (playlistId: string, snapshotId: string): string =>
  `${PLAYLIST_CACHE_PREFIX}${playlistId}:${snapshotId}`;

const prunePlaylistCacheIfNeeded = () => {
  if (!canUseLocalStorage) return;
  try {
    const keys: Array<{ key: string; expiresAt: number }> = [];
    const now = Date.now();
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(PLAYLIST_CACHE_PREFIX)) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw);
        const expiresAt = typeof parsed?.expiresAt === 'number' ? parsed.expiresAt : 0;
        if (!expiresAt || expiresAt <= now || parsed?.version !== PLAYLIST_CACHE_VERSION) {
          localStorage.removeItem(key);
          continue;
        }
        keys.push({ key, expiresAt });
      } catch {
        localStorage.removeItem(key);
      }
    }

    if (keys.length > PLAYLIST_CACHE_MAX_ENTRIES) {
      const excess = keys.length - PLAYLIST_CACHE_MAX_ENTRIES;
      keys.sort((a, b) => a.expiresAt - b.expiresAt);
      for (let index = 0; index < excess; index += 1) {
        localStorage.removeItem(keys[index].key);
      }
    }
  } catch {
    // ignore cache cleanup issues
  }
};

const writePlaylistCache = (
  playlistId: string,
  snapshotId: string,
  tracks: PlaylistItemWithSaved[],
  total?: number | null
) => {
  if (!canUseLocalStorage || !snapshotId) return;
  try {
    prunePlaylistCacheIfNeeded();
    const payload = {
      version: PLAYLIST_CACHE_VERSION,
      expiresAt: Date.now() + PLAYLIST_CACHE_TTL_MS + randomJitter(),
      playlistId,
      snapshotId,
      total: typeof total === 'number' ? total : null,
      tracks,
    };
    localStorage.setItem(makePlaylistCacheKey(playlistId, snapshotId), JSON.stringify(payload));
  } catch {
    // ignore cache write failures
  }
};

const readPlaylistCache = (
  playlistId: string,
  snapshotId?: string | null,
  total?: number | null
): PlaylistItemWithSaved[] | null => {
  if (!canUseLocalStorage || !snapshotId) return null;
  try {
    const raw = localStorage.getItem(makePlaylistCacheKey(playlistId, snapshotId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      localStorage.removeItem(makePlaylistCacheKey(playlistId, snapshotId));
      return null;
    }
    if (parsed.version !== PLAYLIST_CACHE_VERSION) {
      localStorage.removeItem(makePlaylistCacheKey(playlistId, snapshotId));
      return null;
    }
    const expiresAt = typeof parsed.expiresAt === 'number' ? parsed.expiresAt : 0;
    if (!expiresAt || Date.now() > expiresAt) {
      localStorage.removeItem(makePlaylistCacheKey(playlistId, snapshotId));
      return null;
    }
    if (typeof total === 'number' && typeof parsed.total === 'number' && parsed.total !== total) {
      return null;
    }
    return Array.isArray(parsed.tracks) ? (parsed.tracks as PlaylistItemWithSaved[]) : null;
  } catch {
    localStorage.removeItem(makePlaylistCacheKey(playlistId, snapshotId ?? ''));
    return null;
  }
};

const chunkArray = <T>(source: T[], size: number): T[][] => {
  if (size <= 0) {
    return [source.slice()];
  }
  const chunks: T[][] = [];
  for (let index = 0; index < source.length; index += size) {
    chunks.push(source.slice(index, index + size));
  }
  return chunks;
};

const fetchAllPlaylistItems = async (
  playlistId: string,
  total?: number | null
): Promise<PlaylistItem[]> => {
  const items: PlaylistItem[] = [];
  const limit = PLAYLIST_PAGE_SIZE;
  let offset = 0;
  while (true) {
    const { data } = await playlistService.getPlaylistItems(playlistId, { offset, limit });
    if (!Array.isArray(data.items) || data.items.length === 0) {
      break;
    }
    items.push(...data.items);
    offset += data.items.length;
    if (!data.next) {
      break;
    }
    if (typeof total === 'number' && offset >= total) {
      break;
    }
  }
  return items;
};

const fetchSavedTrackStates = async (trackIds: string[]): Promise<boolean[]> => {
  if (!trackIds.length) return [];
  const chunks = chunkArray(trackIds, SAVED_TRACKS_CHUNK_SIZE);
  const responses = await Promise.all(
    chunks.map(async (chunk) => {
      if (!chunk.length) return [];
      try {
        const { data } = await userService.checkSavedTracks(chunk);
        return Array.isArray(data) && data.length === chunk.length
          ? data
          : new Array(chunk.length).fill(false);
      } catch {
        return new Array(chunk.length).fill(false);
      }
    })
  );
  return responses.flat();
};

const buildEnrichedPlaylistItems = async (
  items: PlaylistItem[],
  user: User | null,
  options: { skipSavedState?: boolean } = {}
): Promise<PlaylistItemWithSaved[]> => {
  if (!items.length) {
    return [];
  }

  const trackIds = items.map((item) => item.track.id);
  let savedFlags: boolean[] = new Array(items.length).fill(false);
  if (user && trackIds.length && !options.skipSavedState) {
    const fetchedFlags = await fetchSavedTrackStates(trackIds);
    if (fetchedFlags.length === items.length) {
      savedFlags = fetchedFlags;
    } else {
      savedFlags = [
        ...fetchedFlags,
        ...new Array(Math.max(0, items.length - fetchedFlags.length)).fill(false),
      ];
    }
  }

  const spotifyTrackIds = Array.from(
    new Set(
      items
        .map((item) => extractSpotifyTrackIdFromTrack(item.track))
        .filter((id): id is string => Boolean(id))
    )
  );

  const trackMatches: Record<string, TrackMatchSummary> =
    spotifyTrackIds.length > 0 ? await recordingsService.fetchTrackMatches(spotifyTrackIds) : {};

  const hydratedTracks = hydrateTracksWithMatches(
    items.map((item) => item.track),
    trackMatches
  );

  return items.map((item, index) => ({
    ...item,
    saved: savedFlags[index] ?? false,
    track: hydratedTracks[index],
  }));
};

const initialState: {
  user: User | null;
  recommedations: Track[];
  tracks: PlaylistItemWithSaved[];
  playlist: Playlist | null;

  loading: boolean;
  canEdit: boolean;
  following: boolean;

  order: string;
  view: 'LIST' | 'COMPACT';
} = {
  user: null,
  tracks: [],
  playlist: null,
  recommedations: [],

  loading: true,
  canEdit: false,
  following: false,

  order: 'ALL',
  view: 'LIST',
};

export const fetchPlaylist = createAsyncThunk<
  [Playlist, PlaylistItemWithSaved[], boolean, boolean, User | null, Track[]],
  string
>('playlist/fetchPlaylist', async (id, { getState, dispatch }) => {
  const { auth } = getState() as RootState;
  const user = auth.user ?? null;

  const { data: playlist } = await playlistService.getPlaylist(id);
  const totalTracks = playlist.tracks?.total ?? null;
  const snapshotId = playlist.snapshot_id ?? null;

  const cachedTracks = readPlaylistCache(id, snapshotId, totalTracks);
  let rawItems: PlaylistItem[] = [];
  let itemsWithMetadata: PlaylistItemWithSaved[] = [];
  let usedCache = false;

  if (cachedTracks && cachedTracks.length) {
    itemsWithMetadata = cachedTracks;
    rawItems = cachedTracks;
    usedCache = true;
  } else {
    const { data: firstPage } = await playlistService.getPlaylistItems(id, {
      offset: 0,
      limit: PLAYLIST_PAGE_SIZE,
    });
    rawItems = Array.isArray(firstPage?.items) ? firstPage.items : [];
    itemsWithMetadata = await buildEnrichedPlaylistItems(rawItems, user, {
      skipSavedState: true,
    });
  }

  if (user) {
    void hydrateSavedStatesInBackground(rawItems, user, dispatch as AppDispatch);
  }

  if (!usedCache) {
    void fetchRemainingTracksInBackground(
      id,
      rawItems.length,
      totalTracks,
      dispatch as AppDispatch,
      user,
      {
        skipSavedState: true,
        initialTracks: itemsWithMetadata,
        snapshotId: snapshotId ?? undefined,
      }
    );
  }

  const trackIdsForSeeds = rawItems.map((item) => item.track.id);
  const artistIdsForSeeds = rawItems
    .map((item) => item.track.artists?.[0]?.id)
    .filter((artistId): artistId is string => Boolean(artistId));

  const isMine = Boolean(user?.id && playlist.owner?.id && user.id === playlist.owner.id);
  const canEdit = Boolean(isMine || playlist.collaborative);

  const [followingResponse, ownerResponse] = await Promise.all([
    user ? userService.checkFollowedPlaylist(id) : Promise.resolve({ data: [false] }),
    playlist.owner?.id
      ? userService.getUser(playlist.owner.id)
      : Promise.resolve({ data: playlist.owner ?? null }),
  ]);

  const followingData = followingResponse.data;
  const following = Array.isArray(followingData)
    ? Boolean(followingData[0])
    : Boolean(followingData);
  const owner = (ownerResponse.data ?? playlist.owner ?? null) as User | null;
  const recommendations: Track[] = [];

  // Recommendations endpoint is currently deprecated; skip background fetch to avoid 404s.

  return [playlist, itemsWithMetadata, following, canEdit, owner, recommendations];
});

export const refreshTracks = createAsyncThunk<PlaylistItemWithSaved[], string>(
  'playlist/refreshTracks',
  async (id, { getState }) => {
    const { auth } = getState() as RootState;
    const user = auth.user ?? null;
    try {
      const items = await fetchAllPlaylistItems(id);
      return await buildEnrichedPlaylistItems(items, user);
    } catch (error) {
      console.log(error);
      return [];
    }
  }
);

export const getNextTracks = createAsyncThunk<PlaylistItemWithSaved[]>(
  'playlist/getNextTracks',
  async (_params, { getState }) => {
    const state = getState() as RootState;
    const { playlist, tracks } = state.playlist;
    const user = state.auth.user ?? null;

    if (!playlist) {
      return [];
    }

    const total = playlist.tracks?.total ?? undefined;
    if (typeof total === 'number' && tracks.length >= total) {
      return [];
    }

    const { data } = await playlistService.getPlaylistItems(playlist.id, {
      offset: tracks.length,
      limit: PLAYLIST_PAGE_SIZE,
    });

    if (!Array.isArray(data.items) || !data.items.length) {
      return [];
    }

    return buildEnrichedPlaylistItems(data.items, user);
  }
);

export const refreshPlaylist = createAsyncThunk<Playlist, string>(
  'playlist/refreshPlaylist',
  async (id) => {
    const { data } = await playlistService.getPlaylist(id);
    return data;
  }
);

const playlistSlice = createSlice({
  name: 'playlist',
  initialState,
  reducers: {
    setPlaylist(state, action: PayloadAction<{ playlist: Playlist | null }>) {
      state.playlist = action.payload.playlist;
      if (!action.payload.playlist) {
        state.tracks = [];
        state.following = false;
        state.canEdit = false;
        state.user = null;
        state.loading = true;
        state.view = 'LIST';
      }
    },
    removeTrack(state, action: PayloadAction<{ id: string }>) {
      state.tracks = state.tracks.filter((track) => track.track.id !== action.payload.id);
    },
    setTrackLikeState(state, action: PayloadAction<{ id: string; saved: boolean }>) {
      state.tracks = state.tracks.map((track) =>
        track.track.id === action.payload.id ? { ...track, saved: action.payload.saved } : track
      );
    },
    setTracksSavedState(state, action: PayloadAction<{ trackIds: string[]; savedFlags: boolean[] }>) {
      const map = new Map<string, boolean>();
      action.payload.trackIds.forEach((id, index) => {
        if (typeof id === 'string') {
          map.set(id, action.payload.savedFlags[index] ?? false);
        }
      });
      if (!map.size) return;
      state.tracks = state.tracks.map((track) =>
        map.has(track.track.id) ? { ...track, saved: map.get(track.track.id) ?? false } : track
      );
    },
    setRecommendations(state, action: PayloadAction<{ recommendations: Track[] }>) {
      state.recommedations = action.payload.recommendations;
    },
    setView(state, action: PayloadAction<{ view: 'LIST' | 'COMPACT' }>) {
      state.view = action.payload.view;
    },
    setOrder(state, action: PayloadAction<{ order: string }>) {
      state.order = action.payload.order;
    },
    reorderTracks(state, action: PayloadAction<{ from: number; to: number }>) {
      const tracks = [...state.tracks];
      const [track] = tracks.splice(action.payload.from, 1);
      tracks.splice(action.payload.to, 0, track);
      state.tracks = tracks;
    },
    resetOrder(state, action: PayloadAction<{ order?: string }>) {
      state.order = action.payload.order || 'ALL';
    },
    removeTrackFromRecommendations(state, action: PayloadAction<{ id: string }>) {
      state.recommedations = state.recommedations.filter((track) => track.id !== action.payload.id);
    },
    appendTracks(state, action: PayloadAction<PlaylistItemWithSaved[]>) {
      state.tracks = [...state.tracks, ...action.payload];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPlaylist.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchPlaylist.fulfilled, (state, action) => {
      state.playlist = action.payload[0];
      state.tracks = action.payload[1];
      state.following = action.payload[2];
      state.canEdit = action.payload[3];
      state.user = action.payload[4];
      state.recommedations = action.payload[5];
      state.loading = false;
    });
    builder.addCase(refreshTracks.fulfilled, (state, action) => {
      state.tracks = action.payload;
    });
    builder.addCase(refreshPlaylist.fulfilled, (state, action: PayloadAction<Playlist>) => {
      state.playlist = action.payload;
    });
    builder.addCase(getNextTracks.fulfilled, (state, action) => {
      state.tracks = [...state.tracks, ...action.payload];
    });
  },
});

async function hydrateSavedStatesInBackground(
  items: PlaylistItem[],
  user: User | null,
  dispatch: AppDispatch
): Promise<void> {
  if (!user || !items.length) return;
  const trackIds = items
    .map((item) => item.track.id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
  if (!trackIds.length) return;
  try {
    const savedFlags = await fetchSavedTrackStates(trackIds);
    dispatch(playlistSlice.actions.setTracksSavedState({ trackIds, savedFlags }));
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[playlist] Failed to hydrate saved states', error);
    }
  }
}

async function fetchRecommendationsInBackground(
  playlistId: string,
  trackIdsForSeeds: string[],
  artistIdsForSeeds: string[],
  dispatch: AppDispatch,
  user: User | null
): Promise<void> {
  try {
    const recommendations = trackIdsForSeeds.length
      ? await playlistService
          .getRecommendations({
            seed_tracks: trackIdsForSeeds.slice(0, 5).join(',') || undefined,
            seed_artists: artistIdsForSeeds.slice(0, 5).join(',') || undefined,
            limit: 25,
          })
          .then((res) => res.data.tracks)
          .catch(() => [])
      : user
        ? await userService
            .fetchTopTracks({ limit: 25, timeRange: 'short_term' })
            .then((res) => res.data.items)
        : [];

    dispatch(playlistSlice.actions.setRecommendations({ recommendations }));
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[playlist] Failed to fetch recommendations', { playlistId, error });
    }
  }
}

async function fetchRemainingTracksInBackground(
  playlistId: string,
  alreadyFetched: number,
  total: number | null | undefined,
  dispatch: AppDispatch,
  user: User | null,
  options: { skipSavedState?: boolean; initialTracks?: PlaylistItemWithSaved[]; snapshotId?: string } = {}
): Promise<void> {
  const limit = PLAYLIST_PAGE_SIZE;
  let offset = alreadyFetched;
  const allTracks: PlaylistItemWithSaved[] = Array.isArray(options.initialTracks)
    ? [...options.initialTracks]
    : [];

  while (true) {
    if (typeof total === 'number' && offset >= total) {
      break;
    }

    try {
      const { data } = await playlistService.getPlaylistItems(playlistId, { offset, limit });
      if (!Array.isArray(data.items) || data.items.length === 0) {
        break;
      }

      const enriched = await buildEnrichedPlaylistItems(data.items, user, {
        skipSavedState: options.skipSavedState,
      });
      dispatch(playlistSlice.actions.appendTracks(enriched));
      allTracks.push(...enriched);
      if (options.skipSavedState) {
        void hydrateSavedStatesInBackground(data.items, user, dispatch);
      }

      offset += data.items.length;
      if (!data.next) {
        break;
      }
    } catch (error) {
      console.warn('[playlist] Failed to fetch additional tracks', error);
      break;
    }
  }

  if (typeof total === 'number' && allTracks.length === total && options.snapshotId) {
    writePlaylistCache(playlistId, options.snapshotId, allTracks, total);
  }
}

export const playlistActions = {
  fetchPlaylist,
  refreshTracks,
  getNextTracks,
  refreshPlaylist,
  ...playlistSlice.actions,
};

export default playlistSlice.reducer;
